import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Wrench, GraduationCap, Users, CheckCircle, ArrowRight } from 'lucide-react';

export const Careers = () => {
  return (
    <div data-testid="careers-page">
      {/* Hero Section */}
      <section className="relative py-32 bg-[#005B96]" data-testid="careers-hero">
        <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/qskuuchf_FB_IMG_1771192477688.jpg')] bg-cover bg-center opacity-10" />
        <div className="relative container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-semibold mb-6">
              Join Our Team
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
              Career Opportunities
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Build your career with a trusted local plumbing company serving Yarmouth, Digby, and Shelburne Counties.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 md:py-28 bg-white" data-testid="careers-intro">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Work With Us
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Duncan's Plumbing Heating & Filtration is a locally owned business serving Yarmouth, Digby, and Shelburne Counties. We're always interested in skilled, reliable people who take pride in their work and want to build a long-term career in the plumbing trade.
            </p>
          </div>
        </div>
      </section>

      {/* Positions */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="careers-positions">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Journey Plumbers */}
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-lg border border-slate-100">
              <div className="w-16 h-16 bg-[#E6F2FA] rounded-2xl flex items-center justify-center mb-6">
                <Wrench size={32} className="text-[#005B96]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Journey Plumbers</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We are seeking licensed journey plumbers with a strong work ethic, a professional attitude, and experience in residential and light commercial plumbing.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-700">
                  <CheckCircle size={18} className="text-[#005B96] flex-shrink-0" />
                  <span>Licensed journeyman certification</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <CheckCircle size={18} className="text-[#005B96] flex-shrink-0" />
                  <span>Strong work ethic</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <CheckCircle size={18} className="text-[#005B96] flex-shrink-0" />
                  <span>Professional attitude</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <CheckCircle size={18} className="text-[#005B96] flex-shrink-0" />
                  <span>Residential & light commercial experience</span>
                </li>
              </ul>
            </div>

            {/* Apprenticeships */}
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-lg border border-slate-100">
              <div className="w-16 h-16 bg-[#E6F2FA] rounded-2xl flex items-center justify-center mb-6">
                <GraduationCap size={32} className="text-[#005B96]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Apprenticeships</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We welcome applications from motivated apprentices who are eager to learn, dependable, and committed to growing their skills in the trade.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-700">
                  <CheckCircle size={18} className="text-[#005B96] flex-shrink-0" />
                  <span>Eager to learn</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <CheckCircle size={18} className="text-[#005B96] flex-shrink-0" />
                  <span>Dependable and reliable</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <CheckCircle size={18} className="text-[#005B96] flex-shrink-0" />
                  <span>Committed to growth</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <CheckCircle size={18} className="text-[#005B96] flex-shrink-0" />
                  <span>Team-oriented attitude</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Apply CTA */}
      <section className="py-20 md:py-28 bg-[#005B96]" data-testid="careers-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <Users size={40} className="text-white" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Apply?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            If you're interested in joining our team, we'd love to hear from you.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-[#005B96] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFD700] hover:text-black transition-all shadow-xl"
            data-testid="apply-now-btn"
          >
            <span>Apply Now</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="sticky-cta md:hidden">
        <Link
          to="/contact"
          className="flex items-center justify-center gap-2 bg-[#005B96] text-white w-full py-4 rounded-full font-bold"
        >
          <span>Apply Now</span>
          <ArrowRight size={20} />
        </Link>
      </div>
    </div>
  );
};

export default Careers;
